import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useApi, MODE_LABEL } from "@/lib/useApi";

export default function ProgressPage() {
  const api = useApi();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/progress").then((r) => setP(r.data)); }, [api]);
  if (!p) return <p className="text-slate-500">Loading…</p>;
  const data = p.trend.map((t, i) => ({ n: i + 1, percent: t.percent, accuracy: t.accuracy }));

  return (
    <div className="space-y-6 max-w-5xl" data-testid="results-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Results & progress</h1>
        <p className="text-slate-600 mt-2 text-sm">{p.attempts} submitted attempt(s). Your history stays available whether or not you subscribe.</p>
      </div>
      {!p.attempts ? (
        <Card className="p-10 border-slate-200 text-center text-slate-600" data-testid="no-results">
          No results yet. <Link to="/app/practice" className="text-blue-600 underline">Start a practice session</Link>.
        </Card>
      ) : (
        <>
          <Card className="p-6 border-slate-200">
            <h2 className="font-display font-semibold">Score trend</h2>
            <div className="h-64 mt-4" data-testid="progress-chart">
              <ResponsiveContainer>
                <LineChart data={data}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                  <XAxis dataKey="n" tick={{ fontSize: 12 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="percent" name="Score %" stroke="#2563EB" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke="#0D9488" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 border-slate-200">
              <h2 className="font-display font-semibold">Section accuracy</h2>
              <div className="mt-4 space-y-3">
                {p.sections.map((s) => (
                  <div key={s.section}><div className="flex justify-between text-sm"><span>{s.section}</span><span>{s.accuracy}%</span></div><Progress value={s.accuracy} className="h-2 mt-1" /></div>
                ))}
              </div>
            </Card>
            <Card className="p-6 border-slate-200" data-testid="weak-units">
              <h2 className="font-display font-semibold">Units to revisit</h2>
              {p.weak_units.length ? p.weak_units.map((u) => (
                <div key={u.unit} className="mt-3"><div className="flex justify-between text-sm"><span>{u.unit}</span><span className="text-red-600">{u.accuracy}%</span></div><Progress value={u.accuracy} className="h-2 mt-1" /></div>
              )) : <p className="text-sm text-slate-500 mt-3">No weak units yet — need at least 2 attempted questions in a unit below 60% accuracy.</p>}
            </Card>
          </div>
          <Card className="p-6 border-slate-200">
            <h2 className="font-display font-semibold">History</h2>
            <div className="mt-3 divide-y divide-slate-100">
              {[...p.trend].reverse().map((t) => (
                <Link key={t.id} to={`/app/results/${t.id}`} className="flex justify-between py-3 text-sm hover:bg-slate-50 px-2 rounded" data-testid={`history-${t.id}`}>
                  <span>{MODE_LABEL[t.mode]} · {t.family.replace("_", " ")}</span><span className="font-mono">{t.score}/{t.max_score} · {t.percent}%</span>
                </Link>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
